import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Project as PlatformProject } from '../types/platform';

/**
 * Client UI preferences only — no business/mock data.
 * Projects, workers, metrics, and notifications come from React Query + API.
 */
export type Project = PlatformProject;

export interface Worker {
  id: string;
  name: string;
  status: 'active' | 'idle' | 'error' | 'offline';
  currentTasks: number;
  icon: string;
  description?: string;
  tasksCompleted?: number;
  cpuUsage?: number;
  memoryUsage?: number;
  uptime?: string;
}

export interface DashboardMetric {
  title: string;
  value: string | number;
  change?: number;
}

interface AppState {
  mode: 'light' | 'dark' | 'system';
  setMode: (mode: 'light' | 'dark' | 'system') => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  compactDensity: boolean;
  toggleCompactDensity: () => void;
  // Legacy fields used by some UI routes; kept empty until API endpoints land.
  workers: Worker[];
  metrics: DashboardMetric[];
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      mode: 'dark',
      setMode: (mode) => set({ mode }),
      sidebarCollapsed: true,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      compactDensity: false,
      toggleCompactDensity: () => set((state) => ({ compactDensity: !state.compactDensity })),
      workers: [],
      metrics: [],
    }),
    {
      name: 'ensomirai-storage',
      version: 5,
      migrate: (persisted) => {
        const state = (persisted ?? {}) as Partial<AppState>;
        return {
          mode: state.mode ?? 'dark',
          sidebarCollapsed: true,
          compactDensity: state.compactDensity ?? false,
          workers: [],
          metrics: [],
        };
      },
      partialize: (state) => ({
        mode: state.mode,
        sidebarCollapsed: state.sidebarCollapsed,
        compactDensity: state.compactDensity,
      }),
    }
  )
);
